import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { LoginDetails, LoginDetailsControllerService } from '@crd-api/api';
import {lastValueFrom, Observable, ReplaySubject, throwError} from 'rxjs';
import { environment } from '../../environments/environment';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AlertService } from '../services/alert.service';
import { AuthRefreshResponse } from './auth-refresh-response';
import { buildAuthEndpointUrl } from './auth-url.util';

export interface AuthLoginProviderResponse {
  action?: 'REDIRECT' | 'PASSWORD_RESET_EMAIL_SENT' | 'ACCOUNT_INACTIVE';
  authorizationPath?: string;
}

export interface AuthLogoutResponse {
  logoutUrl?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private static readonly RETURN_URL_KEY = 'crd.returnUrl';

  private http = inject(HttpClient);
  private loginDetailsControllerService = inject(LoginDetailsControllerService);
  private alertService = inject(AlertService);
  private router = inject(Router);

  private currentUserSubject: ReplaySubject<LoginDetails | null>;
  public currentUser: Observable<LoginDetails | null>;
  public currentLoginDetails: LoginDetails | null = null;
  private refreshRequest$: Observable<AuthRefreshResponse> | null = null;
  private initializationPromise: Promise<void> | null = null;
  public isRefreshing = false;

  constructor() {
    // Using a ReplaySubject as opposed to BehaviorSubject since it doesn't require an initial value.
    // Initial value isn't desired since the route guard will often check this value before the async network call completes
    // Size is 1, so it always returns current, so not really replaying
    this.currentUserSubject = new ReplaySubject<LoginDetails | null>(1);
    this.currentUser = this.currentUserSubject.asObservable();

    this.currentUserSubject.subscribe(loginDetails => {
      this.currentLoginDetails = loginDetails;
    });
  }

  public get currentUserValue(): LoginDetails | null {
    return this.currentLoginDetails;
  }

  initialize(): Promise<void> {
    return this.bootstrapSession();
  }

  waitForInitialization(): Promise<void> {
    return this.initializationPromise ?? Promise.resolve();
  }

  async refreshLoginStatus(): Promise<void> {
    try {
      const loginDetails = await lastValueFrom(
        this.fetchCurrentLoginDetails()
          .pipe(
            catchError(() =>
              this.refreshAccessToken().pipe(map(response => response.loginDetails))
            )
          )
      );

      this.currentUserSubject.next(loginDetails);
    } catch {
      this.removeLocalSession();
    }
  }

  login(username: string, password: string) {
    const body = new HttpParams()
      .set('username', username)
      .set('password', password);

    const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');

    return this.http.post<LoginDetails>(`${environment.SERVICE_BASE}/login`, body, {headers})
      .pipe(
        tap(loginDetails => this.currentUserSubject.next(loginDetails)),
        catchError(err => {
          // Specifically handle the 401, since other errors are handled by the global interceptor
          if (err.status === 401) {

            const failureName = err.headers.get('x-auth-failure-name');
            if (failureName === 'SEC_ACCOUNT_STATUS_PROBLEM') {
              this.alertService.addSticky({
                // tslint:disable-next-line:max-line-length
                message: `There is a problem with your account. Please try again after waiting 30 minutes. If the problem continues, please contact the administrator of this application.`,
                id: 0,
                type: 'danger'
              });
            } else if (failureName === 'SEC_PWD_RESET_SENT') {
              this.alertService.addSticky({
                message: `There is a problem with your account sign-in. Please contact your administrator for access assistance.`,
                id: 0,
                type: 'warning'
              });
            } else {
              this.alertService.addSelfClosing({message: `Invalid login, please try again`, id: 0, type: 'warning'});
            }
          }
          return throwError(err);
        }));
  }
  async logout(): Promise<void> {
    let logoutUrl: string | null | undefined;

    try {
      const response = await lastValueFrom(
        this.http.post<AuthLogoutResponse>(
          this.buildAuthUrl('/logout'),
          null,
          { withCredentials: true }
        )
      );
      logoutUrl = response.logoutUrl;

    } catch {
      console.warn('Logout request failed (continuing to log out locally)');
    } finally {
      if (logoutUrl) {
        // Keep the authenticated view stable until the provider navigation begins.
        // Clearing it first renders the anonymous home page before Auth0 returns and renders it again.
        this.redirectToLogoutUrl(logoutUrl);
      } else {
        this.removeLocalSession();
        await this.router.navigate(['home']);
      }
    }
  }

  removeLocalSession() {
    this.currentUserSubject.next(null);
  }

  refreshAccessToken(): Observable<AuthRefreshResponse> {
    if (this.refreshRequest$) {
      return this.refreshRequest$;
    }

    this.isRefreshing = true;
    this.refreshRequest$ = this.http.post<AuthRefreshResponse>(
      this.buildAuthUrl('/refresh'),
      null,
      { withCredentials: true }
    ).pipe(
      tap(response => this.setSession(response)),
      finalize(() => {
        this.isRefreshing = false;
        this.refreshRequest$ = null;
      }),
      shareReplay(1)
    );

    return this.refreshRequest$;
  }

  resolveLoginProvider(email: string): Observable<AuthLoginProviderResponse> {
    return this.http.post<AuthLoginProviderResponse>(
      this.buildAuthUrl('/login-provider'),
      { email },
      { withCredentials: true }
    );
  }

  requestPasswordReset(): Observable<void> {
    return this.http.post<void>(
      this.buildAuthUrl('/password-reset'),
      null,
      { withCredentials: true }
    );
  }

  buildOAuthAuthorizationUrl(authorizationPath: string): string {
    if (/^https?:\/\//i.test(authorizationPath)) {
      return authorizationPath;
    }

    const normalizedPath = authorizationPath.startsWith('/') ? authorizationPath : `/${authorizationPath}`;
    const normalizedServiceBase = (environment.SERVICE_BASE || '').replace(/\/$/, '');
    return `${normalizedServiceBase}${normalizedPath}`;
  }

  isAuthenticated(): boolean {
    return this.currentUserValue !== null && this.currentUserValue.username !== null;
  }

  isAuthenticatedObservable(): Observable<boolean> {
    return this.currentUserSubject.pipe(map(u => {
      return u !== null && u.username !== null;
    }));
  }

  hasRole(role: string): boolean {
    return this.currentUserValue !== null
      && this.currentUserValue.roles !== null
      && this.currentUserValue.roles.includes(role);
  }

  hasAgency(): boolean {
    return Boolean(this.currentUserValue?.userAccountLightDto?.agency?.id);
  }

  storeReturnUrl(returnUrl?: string | null): void {
    if (!returnUrl || returnUrl === '/login') {
      sessionStorage.removeItem(AuthenticationService.RETURN_URL_KEY);
      return;
    }

    sessionStorage.setItem(AuthenticationService.RETURN_URL_KEY, returnUrl);
  }

  consumeReturnUrl(): string {
    const returnUrl = sessionStorage.getItem(AuthenticationService.RETURN_URL_KEY) || '/';
    sessionStorage.removeItem(AuthenticationService.RETURN_URL_KEY);
    return returnUrl;
  }

  private async bootstrapSession(): Promise<void> {
    try {
      await lastValueFrom(this.refreshAccessToken());
    } catch {
      this.removeLocalSession();
    }
  }

  private fetchCurrentLoginDetails(): Observable<LoginDetails> {
    return this.loginDetailsControllerService.getCurrentLoginDetails().pipe(
      tap(loginDetails => this.currentUserSubject.next(loginDetails))
    );
  }

  private setSession(response: AuthRefreshResponse): void {
    this.currentUserSubject.next(response.loginDetails);
  }

  private buildAuthUrl(path: string): string {
    return buildAuthEndpointUrl(environment.SERVICE_BASE || window.location.origin, environment.SERVICE_CONTEXT, path);
  }

  protected redirectToLogoutUrl(url: string): void {
    window.location.href = url;
  }
}
